import { useEffect, useState, useRef } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone, ChevronDown, ExternalLink } from "lucide-react";
import { SITE } from "@/lib/site";

const NAV_ITEMS = [
  { to: "/", label: "Home" },
  { to: "/services", label: "What We Do" },
  { to: "/team", label: "Meet Our Team" },
  { to: "/resources", label: "Resources" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [othersOpen, setOthersOpen] = useState(false);
  const othersRef = useRef(null);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); setOthersOpen(false); }, [pathname]);

  useEffect(() => {
    const onClick = (e) => {
      if (othersRef.current && !othersRef.current.contains(e.target)) setOthersOpen(false);
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-cream-50/90 backdrop-blur-md border-b border-navy-100/80"
          : "bg-cream-50/60 backdrop-blur-sm border-b border-transparent"
      }`}
    >
      <div className="container-narrow flex items-center justify-between gap-3 h-[92px]">
        <Link to="/" data-testid="navbar-logo-link" className="flex items-center gap-3 group shrink-0 min-w-0">
          <img src={SITE.logo} alt="St. John Accounting & Tax logo" className="h-16 w-16 xl:h-20 xl:w-20 rounded-full object-cover ring-1 ring-navy-100 bg-white shrink-0" />
          <div className="leading-tight whitespace-nowrap min-w-0">
            <div className="font-serif-display text-[18px] xl:text-[20px] 2xl:text-[22px] font-semibold text-navy-800 tracking-tight">
              St. John <span className="italic text-gold-500">Accounting</span> & Tax
            </div>
            <div className="text-[10px] xl:text-[10.5px] uppercase tracking-[0.2em] text-navy-500/80">Owasso · Tulsa</div>
          </div>
        </Link>

        <nav className="hidden xl:flex items-center gap-0.5 2xl:gap-1 min-w-0" data-testid="navbar-desktop-menu">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `relative px-2.5 2xl:px-3.5 py-2 rounded text-[13.5px] 2xl:text-[14.5px] font-medium transition-colors whitespace-nowrap ${
                  isActive ? "text-navy-800" : "text-navy-600 hover:text-navy-800"
                }`
              }
              end={item.to === "/"}
            >
              {({ isActive }) => (
                <>
                  {item.label}
                  {isActive && <span className="absolute bottom-0 left-2.5 right-2.5 2xl:left-3.5 2xl:right-3.5 h-px bg-gold-500" />}
                </>
              )}
            </NavLink>
          ))}

          {/* Related Services dropdown */}
          <div className="relative" ref={othersRef}>
            <button
              type="button"
              onClick={() => setOthersOpen((o) => !o)}
              data-testid="nav-link-other-companies"
              aria-expanded={othersOpen}
              className="inline-flex items-center gap-1 px-2.5 2xl:px-3.5 py-2 rounded text-[13.5px] 2xl:text-[14.5px] font-medium text-navy-600 hover:text-navy-800 transition-colors whitespace-nowrap"
            >
              Related Services
              <ChevronDown size={14} className={`transition-transform ${othersOpen ? "rotate-180" : ""}`} />
            </button>
            {othersOpen && (
              <div className="absolute right-0 mt-2 w-72 rounded border border-navy-100 bg-white shadow-lg overflow-hidden z-50" data-testid="nav-others-dropdown">
                {SITE.otherCompanies.map((c) => (
                  <a
                    key={c.url}
                    href={c.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`nav-other-${c.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className="flex items-start justify-between gap-3 px-4 py-3 hover:bg-cream-100/70 transition-colors border-b border-navy-50 last:border-b-0"
                  >
                    <div>
                      <div className="font-medium text-navy-800 text-[14px]">{c.name}</div>
                      <div className="text-navy-500 text-[12px] mt-0.5">{c.desc}</div>
                    </div>
                    <ExternalLink size={14} className="text-gold-500 mt-1 shrink-0" />
                  </a>
                ))}
              </div>
            )}
          </div>
        </nav>

        <div className="hidden xl:flex items-center gap-3 shrink-0">
          <a href={SITE.bookingUrl} target="_blank" rel="noopener noreferrer" data-testid="navbar-book-cta" className="btn-primary !py-2 !px-4 2xl:!py-2.5 2xl:!px-5 text-[13px] 2xl:text-[13.5px] whitespace-nowrap">Book Consultation</a>
        </div>

        <button
          data-testid="navbar-mobile-toggle"
          aria-label="Toggle navigation menu"
          aria-expanded={open}
          className="xl:hidden p-2 rounded text-navy-800 hover:bg-navy-50 transition-colors"
          onClick={() => setOpen((o) => !o)}
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <div
        className={`xl:hidden overflow-hidden transition-[max-height,opacity] duration-300 ease-out border-t border-navy-100 ${
          open ? "max-h-[560px] opacity-100" : "max-h-0 opacity-0"
        }`}
        data-testid="navbar-mobile-menu"
      >
        <div className="container-narrow py-4 flex flex-col gap-1">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-mobile-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `px-4 py-3 rounded text-[15px] font-medium ${isActive ? "text-navy-800 bg-navy-50" : "text-navy-600 hover:text-navy-800 hover:bg-navy-50/70"}`
              }
              end={item.to === "/"}
            >
              {item.label}
            </NavLink>
          ))}
          <div className="mt-2 pt-2 border-t border-navy-100">
            <div className="px-4 pt-2 pb-1 text-[10.5px] uppercase tracking-[0.22em] text-navy-500">Related Services</div>
            {SITE.otherCompanies.map((c) => (
              <a key={c.url} href={c.url} target="_blank" rel="noopener noreferrer" data-testid={`nav-mobile-other-${c.name.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="block px-4 py-2.5 text-[14px] text-navy-600 hover:text-navy-800">
                {c.name}
                <span className="text-[11px] text-navy-400 ml-2">↗</span>
              </a>
            ))}
          </div>
          <div className="flex flex-col gap-2 mt-2 pt-3 border-t border-navy-100">
            <a href={SITE.phoneTel} data-testid="navbar-mobile-phone" className="inline-flex items-center gap-2 text-sm text-navy-700 px-4 py-2"><Phone size={16} className="text-gold-500" />{SITE.phone}</a>
            <a href={SITE.bookingUrl} target="_blank" rel="noopener noreferrer" data-testid="navbar-mobile-book-cta" className="btn-primary mx-4 mb-2 text-sm">Book Consultation</a>
          </div>
        </div>
      </div>
    </header>
  );
}
